<script setup>
/* global axios */
// Config do nó "Base de Conhecimento": seleciona/cria uma base, faz upload de
// documentos (PDF/TXT/MD/CSV/JSON), lista com status da ingestão e permite excluir.
// O aiAgent conectado a este nó (handle "ferramentas") consulta a base (RAG).
import { ref, computed, inject, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({ node: { type: Object, required: true } });
const { t } = useI18n();
const aux = inject('flowAux', {});
const d = computed(() => props.node.data);

const base = computed(
  () => `/api/v1/accounts/${aux.accountId}/kanban/knowledge_bases`
);

const bases = ref([]);
const documents = ref([]);
const loading = ref(false);
const creating = ref(false);
const newName = ref('');
const uploading = ref(false);
const errorMsg = ref('');
const fileInput = ref(null);
let pollTimer = null;

const selectedId = computed(() => d.value.knowledgeBaseId || '');
const hasSelection = computed(() => !!selectedId.value);

const readyCount = computed(
  () => documents.value.filter(doc => doc.status === 'ready').length
);
const anyProcessing = computed(() =>
  documents.value.some(
    doc => doc.status === 'pending' || doc.status === 'processing'
  )
);

const STATUS = {
  pending: { label: 'CHATBOT.nodeConfig.knowledgeBase.statusPending', cls: 'text-n-amber-11 bg-n-amber-3' },
  processing: { label: 'CHATBOT.nodeConfig.knowledgeBase.statusProcessing', cls: 'text-n-blue-11 bg-n-blue-3' },
  ready: { label: 'CHATBOT.nodeConfig.knowledgeBase.statusReady', cls: 'text-n-teal-11 bg-n-teal-3' },
  failed: { label: 'CHATBOT.nodeConfig.knowledgeBase.statusFailed', cls: 'text-n-ruby-11 bg-n-ruby-3' },
};

const loadBases = async () => {
  try {
    const { data } = await axios.get(base.value);
    bases.value = data.data || [];
  } catch (e) {
    bases.value = [];
  }
};

const loadDocuments = async () => {
  if (!hasSelection.value) {
    documents.value = [];
    return;
  }
  try {
    const { data } = await axios.get(`${base.value}/${selectedId.value}/documents`);
    documents.value = data.data || [];
    d.value.documentsCount = readyCount.value;
    schedulePoll();
  } catch (e) {
    documents.value = [];
  }
};

const schedulePoll = () => {
  clearTimeout(pollTimer);
  if (anyProcessing.value) pollTimer = setTimeout(loadDocuments, 3000);
};

const selectBase = id => {
  const kb = bases.value.find(b => String(b.id) === String(id));
  d.value.knowledgeBaseId = id ? Number(id) : '';
  d.value.name = kb ? kb.name : '';
  d.value.documentsCount = 0;
  loadDocuments();
};

const createBase = async () => {
  const name = newName.value.trim();
  if (!name) return;
  loading.value = true;
  errorMsg.value = '';
  try {
    const { data } = await axios.post(base.value, { name });
    bases.value.unshift(data.data);
    creating.value = false;
    newName.value = '';
    selectBase(data.data.id);
  } catch (e) {
    errorMsg.value = e?.response?.data?.error || 'Falha ao criar a base';
  } finally {
    loading.value = false;
  }
};

const upload = async e => {
  const file = e.target.files[0];
  if (!file || !hasSelection.value) return;
  uploading.value = true;
  errorMsg.value = '';
  try {
    const fd = new FormData();
    fd.append('file', file);
    await axios.post(`${base.value}/${selectedId.value}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    await loadDocuments();
  } catch (err) {
    errorMsg.value =
      err?.response?.data?.error || 'Falha no upload do documento';
  } finally {
    uploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};

const removeDoc = async docId => {
  try {
    await axios.delete(`${base.value}/${selectedId.value}/documents/${docId}`);
    await loadDocuments();
  } catch (e) {
    errorMsg.value = 'Falha ao excluir o documento';
  }
};

const fmtSize = bytes => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

onMounted(async () => {
  loading.value = true;
  await loadBases();
  await loadDocuments();
  loading.value = false;
});
onBeforeUnmount(() => clearTimeout(pollTimer));
</script>

<template>
  <div>
    <!-- seletor / criação de base -->
    <label class="block mb-1 text-sm font-medium text-n-slate-12">
      {{ t('CHATBOT.nodeConfig.knowledgeBase.baseLabel') }}
    </label>

    <div v-if="!creating" class="flex items-center gap-2 mb-3">
      <select
        :value="selectedId"
        class="flex-1 h-9 px-2 text-sm border rounded-md border-n-weak bg-n-alpha-1 text-n-slate-12"
        @change="selectBase($event.target.value)"
      >
        <option value="">
          {{ t('CHATBOT.nodeConfig.knowledgeBase.basePlaceholder') }}
        </option>
        <option v-for="b in bases" :key="b.id" :value="b.id">
          {{ b.name }} ({{ b.documents_count }})
        </option>
      </select>
      <button
        type="button"
        class="h-9 px-3 text-sm rounded-md whitespace-nowrap bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3"
        @click="creating = true"
      >
        {{ t('CHATBOT.nodeConfig.knowledgeBase.newBase') }}
      </button>
    </div>

    <div v-else class="flex items-center gap-2 mb-3">
      <input
        v-model="newName"
        type="text"
        :placeholder="t('CHATBOT.nodeConfig.knowledgeBase.newBasePlaceholder')"
        class="flex-1 h-9 px-2 text-sm border rounded-md border-n-weak bg-n-alpha-1 text-n-slate-12"
        @keyup.enter="createBase"
      />
      <button
        type="button"
        :disabled="loading || !newName.trim()"
        class="h-9 px-3 text-sm text-white rounded-md whitespace-nowrap bg-n-brand disabled:opacity-50"
        @click="createBase"
      >
        {{ t('CHATBOT.nodeConfig.knowledgeBase.create') }}
      </button>
      <button
        type="button"
        class="h-9 px-2 text-sm rounded-md text-n-slate-11"
        @click="creating = false"
      >
        {{ t('CHATBOT.nodeConfig.knowledgeBase.cancel') }}
      </button>
    </div>

    <!-- documentos -->
    <template v-if="hasSelection">
      <div class="flex items-center justify-between mb-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CHATBOT.nodeConfig.knowledgeBase.documentsTitle') }}
        </label>
        <span class="text-[0.6875rem] text-n-slate-10">
          {{ readyCount }} {{ t('CHATBOT.nodeConfig.knowledgeBase.previewDocuments') }}
        </span>
      </div>

      <input
        ref="fileInput"
        type="file"
        accept=".pdf,.txt,.md,.markdown,.csv,.json,.log,application/pdf,text/plain"
        class="hidden"
        :disabled="uploading"
        @change="upload"
      />
      <button
        type="button"
        :disabled="uploading"
        class="flex items-center justify-center w-full gap-2 px-3 mb-1 text-sm font-medium border border-dashed rounded-md h-9 border-n-weak text-n-slate-11 hover:border-n-brand hover:text-n-brand hover:bg-n-alpha-1 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        @click="fileInput?.click()"
      >
        <span
          :class="uploading ? 'i-ph-circle-notch animate-spin' : 'i-ph-upload-simple'"
          class="size-4 shrink-0"
        />
        <span class="truncate">
          {{
            uploading
              ? t('CHATBOT.nodeConfig.knowledgeBase.uploading')
              : t('CHATBOT.nodeConfig.knowledgeBase.uploadButton')
          }}
        </span>
      </button>
      <p class="mb-2 text-[0.625rem] text-n-slate-10">
        {{ t('CHATBOT.nodeConfig.knowledgeBase.uploadHint') }}
      </p>

      <div class="flex flex-col gap-1.5">
        <div
          v-for="doc in documents"
          :key="doc.id"
          class="flex items-center gap-2 p-2 border rounded-md border-n-weak bg-n-alpha-1"
        >
          <span class="i-ph-file-text text-n-slate-10 size-4 shrink-0" />
          <div class="flex-1 min-w-0">
            <div class="text-xs truncate text-n-slate-12">{{ doc.name }}</div>
            <div class="text-[0.625rem] text-n-slate-10">
              {{ fmtSize(doc.byte_size) }}
              <template v-if="doc.status === 'ready'">
                · {{ doc.chunk_count }} {{ t('CHATBOT.nodeConfig.knowledgeBase.chunks') }}
              </template>
              <template v-if="doc.status === 'failed' && doc.error_message">
                · {{ doc.error_message }}
              </template>
            </div>
          </div>
          <span
            class="text-[0.625rem] font-medium px-1.5 py-0.5 rounded shrink-0"
            :class="(STATUS[doc.status] || {}).cls"
          >
            {{ t((STATUS[doc.status] || {}).label || '') }}
          </span>
          <button
            type="button"
            class="flex shrink-0 text-n-slate-10 hover:text-n-ruby-11"
            @click="removeDoc(doc.id)"
          >
            <span class="i-ph-trash size-4" />
          </button>
        </div>
        <p
          v-if="!documents.length"
          class="text-xs italic text-n-slate-10 py-1"
        >
          {{ t('CHATBOT.nodeConfig.knowledgeBase.noDocuments') }}
        </p>
      </div>

      <p class="mt-3 text-xs text-n-slate-10">
        {{ t('CHATBOT.nodeConfig.knowledgeBase.previewUsageHint') }}
      </p>
    </template>

    <p v-if="errorMsg" class="mt-2 text-xs text-n-ruby-11">{{ errorMsg }}</p>
  </div>
</template>
